import { ApiError, apiService } from './api';

/** Mirrors the public API's trimmed shape — the endpoint deliberately omits internal fields. */
export interface LegalDocument {
  /** The document id, stable across every publish. */
  _id: string;
  /** Slug of the title; the readable half of the document's url. */
  key: string;
  title: string;
  locale: string;
  version: number;
  /** Absent on the list response, which projects the body away — it is large. */
  bodyHtml?: string;
  /** What changed since the last version. Sent with the body; empty when the admin left it blank. */
  summaryOfChanges?: string;
  effectiveFrom?: string;
}

interface LegalDocumentListResponse {
  result?: LegalDocument[];
}

interface LegalDocumentResponse {
  result?: LegalDocument;
}

// Unauthenticated on purpose: the footer renders for logged-out visitors, so
// these routes are mounted under `/public` with no token middleware.
export const legalService = {
  /** Documents the admin has published and made live, metadata only. */
  async getDocuments(): Promise<LegalDocument[]> {
    const response = await apiService.get<LegalDocumentListResponse>(
      '/public/api/v1/legal/documents',
    );
    return response.result ?? [];
  },

  /**
   * The full document including its body, addressed by id or by key — the endpoint accepts
   * either, so a caller that already knows the key does not have to resolve an id first.
   */
  async getDocument(idOrKey: string): Promise<LegalDocument | null> {
    try {
      const response = await apiService.get<LegalDocumentResponse>(
        `/public/api/v1/legal/documents/${encodeURIComponent(idOrKey)}`,
      );
      return response.result ?? null;
    } catch (error) {
      // Hidden or never published is an answer, not a failure — the caller decides what to do with it.
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  },
};
